import type { components } from "@oathera/contract/api";
import { SERVICES, type Service } from "@oathera/contract";
import type { ApiError } from "../../lib/api.ts";
import type { StatusLevel } from "../../components/index.ts";

/**
 * Presentation vocabulary and the write-validation model for the Sources screen (task 11.14 / T9.14;
 * design 11 operator-API Sources row, 12.2 Sources row, 12.3 principles, 12.4 a11y, design 13 source
 * management). An ADMIN write screen, so this file is the single, testable home for:
 *
 * - the contract-derived types for every sources operation (list / create / register key / revoke /
 *   approve audit key) — taken straight from `@oathera/contract/api` `components["schemas"]`, never
 *   redefined here (do NOT invent fields);
 * - the CRITICAL never-see-private-keys invariant ({@link validatePublicJwk}): the enrol wizard and
 *   the key-registration form register ONLY a public JWK. A pasted JWK that carries ANY private
 *   member (`d`, `p`, `q`, `dp`, `dq`, `qi`, `k`, or an `oth` array) is REJECTED client-side before
 *   it is ever sent — the control plane never generates or sees a private key (FR-11.1). The server
 *   re-checks (the `PublicJwk` schema is `additionalProperties: false`, so a private member is a
 *   400), so this is UX / defence in depth; the server stays the authority;
 * - the human labels, status-level mappings (icon+text, never colour alone; design 12.4) and honest
 *   formatters the regions share;
 * - the error-to-sentence helpers so a 403 reads "the server did not permit this" (admin required)
 *   and a 400/422 reads as the server's own validation message.
 *
 * It defines no API shapes and does no fetching (that is `use-sources.ts`).
 */

type Schemas = components["schemas"];

/* ---- Contract-derived types ----------------------------------------------------------------- */

/** One registered source with status, liveness, last seq, drops, skew, keys and audit pins. */
export type Source = Schemas["Source"];
/** One page of sources (`GET /sources`). */
export type SourcePage = Schemas["SourcePage"];
/** The lifecycle status of a source (not its live/silent liveness). */
export type SourceStatus = Schemas["SourceStatus"];
/** The create-source body (tenant is NEVER in the body; it comes from the verified identity). */
export type SourceInput = Schemas["SourceInput"];
/** The create-source result (source + audit receipt). */
export type SourceResult = Schemas["SourceResult"];

/** A source's audit-key policy: `pinned` (verify against pinned keys) or `tofu` (demo only). */
export type AuditKeyPolicy = Schemas["AuditKeyPolicy"];
/** A source's admin deep-link registration status (FR-1.5). */
export type AdminLink = Schemas["AdminLink"];

/** A public telemetry JWK (NO private members — the schema forbids them). */
export type PublicJwk = Schemas["PublicJwk"];
/** One telemetry key with its state (active / retiring / revoked) and validity window. */
export type TelemetryKey = Schemas["TelemetryKey"];
/** One audit-signing-key pin, carrying its state and any re-verification progress. */
export type AuditKeyPin = Schemas["AuditKeyPin"];
/** The pin state: `pinned`, `observed_unpinned` (seen but not approved) or `retired`. */
export type AuditKeyState = Schemas["AuditKeyState"];
/** Retroactive re-verification progress after an audit-key approval (FR-6.6). */
export type Reverification = Schemas["Reverification"];

/** The register-key body: a telemetry public key (rotation) OR an audit-key pin. */
export type SourceKeyInput = Schemas["SourceKeyInput"];
/** The telemetry-key branch of {@link SourceKeyInput} (`purpose: telemetry`). */
export type TelemetryKeyInput = Schemas["TelemetryKeyInput"];
/** The audit-pin branch of {@link SourceKeyInput} (`purpose: audit`). */
export type AuditKeyPinInput = Schemas["AuditKeyPinInput"];
/** The register-key result (telemetry key or pin, the retired key if any, audit receipt). */
export type SourceKeyResult = Schemas["SourceKeyResult"];

/** The revoke body (optional `kid` for a key; absent revokes the whole source; reason required). */
export type RevokeRequest = Schemas["RevokeRequest"];
/** The revoke result (source + scope + 30 s propagation window + audit receipt). */
export type RevokeResult = Schemas["RevokeResult"];

/** The approve-audit-key body (reason required). */
export type AuditKeyApproveRequest = Schemas["AuditKeyApproveRequest"];
/** The approve-audit-key result (pin + queued re-verification + audit receipt). */
export type AuditKeyApproveResult = Schemas["AuditKeyApproveResult"];

/** The audit receipt a write appends. */
export type AuditReceipt = Schemas["AuditReceipt"];

/* ---- Enum option sets (straight from the contract) ------------------------------------------ */

/** The services a source can declare (the single source of truth is `@oathera/contract`). */
export const SERVICE_OPTIONS: readonly Service[] = SERVICES;
/** The audit-key policies the enrol wizard offers (`tofu` is explicit, demo only; FR-11.5). */
export const AUDIT_KEY_POLICY_OPTIONS: readonly AuditKeyPolicy[] = ["pinned", "tofu"];
/** The source statuses the list filter offers. */
export const SOURCE_STATUS_OPTIONS: readonly SourceStatus[] = ["active", "disabled", "revoked"];

/* ---- The never-see-private-keys invariant (the keystone of this screen) --------------------- */

/**
 * The private JWK members. A JWK carrying ANY of these describes a private key, and the control
 * plane must NEVER receive one (FR-11.1). `oth` is the RSA other-primes array (multi-prime private
 * keys). This set is matched case-insensitively against the pasted object's keys.
 */
export const PRIVATE_JWK_MEMBERS: readonly string[] = ["d", "p", "q", "dp", "dq", "qi", "k", "oth"];

/** The public members the contract's `PublicJwk` allows (schema `additionalProperties: false`). */
const ALLOWED_JWK_MEMBERS: readonly string[] = [
  "kty",
  "crv",
  "x",
  "y",
  "n",
  "e",
  "kid",
  "alg",
  "use",
];

/** The result of parsing + validating a pasted public JWK, ready to send or with the reason why not. */
export interface PublicJwkValidation {
  /** True when the pasted text is a well-formed PUBLIC JWK the server will accept. */
  readonly ok: boolean;
  /** The message to show when `ok` is false. */
  readonly message?: string;
  /** The parsed public JWK to send when `ok`; undefined otherwise. */
  readonly jwk?: PublicJwk;
  /**
   * True when the text looked like a PRIVATE key (it carried a private member). Surfaced distinctly
   * so the UI can show the strongest refusal: the control plane never accepts a private key.
   */
  readonly privateKeyRejected: boolean;
}

/**
 * Validate pasted JSON as a PUBLIC JWK the control plane may register — the client half of the
 * never-see-private-keys invariant (FR-11.1; design 13 §2).
 *
 * It parses the text, then REFUSES, in this order:
 * - not valid JSON, or not a JSON object → "paste a JSON object";
 * - carries ANY private member (`d`, `p`, `q`, `dp`, `dq`, `qi`, `k`, `oth`) → a hard refusal:
 *   this is a PRIVATE key and the control plane must never receive it. `privateKeyRejected` is set
 *   so the UI can shout about it. The value is NEVER sent;
 * - missing the required `kty`, or an unknown `kty` → explain the required public members;
 * - carries an unknown member the `PublicJwk` schema forbids → name it (the server's
 *   `additionalProperties: false` would 400 anyway);
 * - missing the public coordinates a given `kty` needs (OKP/EC need `x`; EC also `y`; RSA needs
 *   `n` and `e`) → explain what is missing.
 *
 * On success it returns a NORMALISED public JWK containing only the allowed members, so even if the
 * paste carried extra whitespace or benign-but-unknown keys we never forward anything unexpected.
 * The server re-validates and remains the authority; this only blocks a request certain to fail and,
 * crucially, GUARANTEES a private key never leaves the browser.
 */
export function validatePublicJwk(text: string): PublicJwkValidation {
  const trimmed = text.trim();
  if (trimmed.length === 0) {
    return { ok: false, message: "Paste the component's PUBLIC JWK (a JSON object).", privateKeyRejected: false };
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(trimmed);
  } catch {
    return { ok: false, message: "That is not valid JSON. Paste the public JWK as a JSON object.", privateKeyRejected: false };
  }

  if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
    return { ok: false, message: "A JWK is a JSON object. Paste the public key, not an array or a bare value.", privateKeyRejected: false };
  }

  const obj = parsed as Record<string, unknown>;
  const keys = Object.keys(obj);
  const lower = new Set(keys.map((k) => k.toLowerCase()));

  // The hard refusal: any private member means this is a private key. Never send it.
  const offending = PRIVATE_JWK_MEMBERS.find((m) => lower.has(m));
  if (offending !== undefined) {
    return {
      ok: false,
      privateKeyRejected: true,
      message:
        `This is a PRIVATE key (it contains the member “${offending}”). The control plane never ` +
        "accepts a private key. Generate the key pair in the component's environment and paste ONLY " +
        "the public JWK (its public members, e.g. kty/crv/x or kty/n/e).",
    };
  }

  const kty = obj["kty"];
  if (typeof kty !== "string") {
    return { ok: false, message: "The JWK is missing its key type (`kty`).", privateKeyRejected: false };
  }
  if (kty !== "OKP" && kty !== "EC" && kty !== "RSA") {
    return { ok: false, message: `Unsupported key type “${kty}”. Use OKP, EC or RSA.`, privateKeyRejected: false };
  }

  // Reject members the PublicJwk schema forbids (additionalProperties: false). This also catches a
  // private member spelled with unusual casing that slipped past the set above.
  const unknown = keys.find((k) => !ALLOWED_JWK_MEMBERS.includes(k));
  if (unknown !== undefined) {
    return {
      ok: false,
      // If the unknown member looks like a private one, treat it as a private-key rejection.
      privateKeyRejected: PRIVATE_JWK_MEMBERS.includes(unknown.toLowerCase()),
      message: `The JWK has an unexpected member “${unknown}”. Only public members are accepted.`,
    };
  }

  // Required public coordinates per key type.
  if ((kty === "OKP" || kty === "EC") && typeof obj["x"] !== "string") {
    return { ok: false, message: `A ${kty} public key needs its \`x\` coordinate.`, privateKeyRejected: false };
  }
  if (kty === "EC" && typeof obj["y"] !== "string") {
    return { ok: false, message: "An EC public key needs its `y` coordinate.", privateKeyRejected: false };
  }
  if (kty === "RSA" && (typeof obj["n"] !== "string" || typeof obj["e"] !== "string")) {
    return { ok: false, message: "An RSA public key needs its modulus `n` and exponent `e`.", privateKeyRejected: false };
  }

  if (typeof obj["use"] === "string" && obj["use"] !== "sig") {
    return { ok: false, message: "A telemetry key must have `use` of `sig` (or omit it).", privateKeyRejected: false };
  }

  // Build a normalised PublicJwk carrying ONLY the public members present. This is the single value
  // the caller forwards — a private member can never reach the body because it is not copied here.
  const jwk: Record<string, unknown> = { kty };
  for (const member of ALLOWED_JWK_MEMBERS) {
    if (member === "kty") continue;
    if (typeof obj[member] === "string") jwk[member] = obj[member];
  }

  return { ok: true, jwk: jwk as PublicJwk, privateKeyRejected: false };
}

/* ---- The operator reason (required on revoke and approve; FR-10.5 / FR-11) ------------------ */

/** Validate an operator reason client-side (mandatory, non-empty after trimming, within 256). */
export function validateReason(reason: string): string | null {
  const trimmed = reason.trim();
  if (trimmed.length === 0) return "A reason is required and recorded in the audit log.";
  if (trimmed.length > 256) return "The reason must be at most 256 characters.";
  return null;
}

/** Validate a `kid` the register-key / pin form supplies (non-empty, within the Id bound). */
export function validateKid(kid: string): string | null {
  const trimmed = kid.trim();
  if (trimmed.length === 0) return "A key id (`kid`) is required.";
  if (trimmed.length > 256) return "The key id is too long.";
  return null;
}

/** A JWK thumbprint is an RFC 7638 base64url SHA-256 (exactly 43 base64url chars). */
export function validateThumbprint(thumbprint: string): string | null {
  const trimmed = thumbprint.trim();
  if (trimmed.length === 0) return "A JWK thumbprint is required to pin an audit key.";
  if (!/^[A-Za-z0-9_-]{43}$/.test(trimmed)) {
    return "A JWK thumbprint is a base64url SHA-256 value of 43 characters: A to Z, a to z, 0 to 9, plus the characters minus and underscore.";
  }
  return null;
}

/* ---- Create-source validation --------------------------------------------------------------- */

/** The create-source form values the enrol wizard binds to. */
export interface SourceFormValues {
  readonly service: Service | "";
  readonly name: string;
  readonly environment: string;
  readonly adminUrl: string;
  readonly jwksUrl: string;
  readonly auditKeyPolicy: AuditKeyPolicy;
}

/** A field-level problem keyed so the wizard can show it beside the control. */
export interface SourceValidationIssue {
  readonly field: "service" | "name" | "environment" | "admin_url" | "jwks_url";
  readonly message: string;
}

/** The result of validating the create-source form client-side. */
export interface SourceValidation {
  readonly ok: boolean;
  readonly issues: readonly SourceValidationIssue[];
  readonly input?: SourceInput;
}

/**
 * Validate the create-source form against the contract's `SourceInput` BEFORE the API is called.
 * The tenant is NEVER part of the body (it comes from the verified identity). Mirrors the contract:
 * `service` is a known enum; `name` 1–128; `environment` 1–64; the optional `admin_url` / `jwks_url`
 * must be https URLs within 2048 chars. The server re-checks and remains the authority.
 */
export function validateSourceInput(values: SourceFormValues): SourceValidation {
  const issues: SourceValidationIssue[] = [];

  if (values.service === "" || !SERVICE_OPTIONS.includes(values.service)) {
    issues.push({ field: "service", message: "Choose the component's service." });
  }

  const name = values.name.trim();
  if (name.length === 0) issues.push({ field: "name", message: "A source name is required." });
  else if (name.length > 128) issues.push({ field: "name", message: "The name must be at most 128 characters." });

  const environment = values.environment.trim();
  if (environment.length === 0) {
    issues.push({ field: "environment", message: "An environment is required (e.g. prod, staging)." });
  } else if (environment.length > 64) {
    issues.push({ field: "environment", message: "The environment must be at most 64 characters." });
  }

  const adminUrl = values.adminUrl.trim();
  if (adminUrl.length > 0) {
    const err = validateHttpsUrl(adminUrl);
    if (err !== null) issues.push({ field: "admin_url", message: err });
  }

  const jwksUrl = values.jwksUrl.trim();
  if (jwksUrl.length > 0) {
    const err = validateHttpsUrl(jwksUrl);
    if (err !== null) issues.push({ field: "jwks_url", message: err });
  }

  if (issues.length > 0) return { ok: false, issues };

  const input: SourceInput = {
    service: values.service as Service,
    name,
    environment,
    audit_key_policy: values.auditKeyPolicy,
  };
  if (adminUrl.length > 0) input.admin_url = adminUrl;
  if (jwksUrl.length > 0) input.jwks_url = jwksUrl;

  return { ok: true, issues: [], input };
}

/** The optional https URLs must be https and within the contract's 2048-char bound. */
function validateHttpsUrl(url: string): string | null {
  if (!/^https:\/\//i.test(url)) return "The URL must start with https://.";
  if (url.length > 2048) return "The URL is too long.";
  return null;
}

/* ---- Status levels (icon + text, never colour alone; design 12.4) --------------------------- */

/**
 * The status level for a source, folding the registered status with the live/silent liveness. A
 * revoked source is an error; a disabled one is neutral (operator action, not a fault). An active
 * source's level comes from its liveness: `live` is neutral (infra presence, never the healthy
 * green — a source being connected is not an app-health judgement; design 12.3), `silent` warns, and
 * `never_seen` is unknown (we have no confirmation it ever reported — never green).
 */
export function sourceLevel(source: Source, liveness: SourceLivenessState): StatusLevel {
  if (source.status === "revoked") return "error";
  if (source.status === "disabled") return "neutral";
  switch (liveness) {
    case "live":
      return "neutral";
    case "silent":
      return "warn";
    case "never_seen":
      return "unknown";
  }
}

/** The level for a telemetry key's state. Revoked is error; retiring warns; active is neutral. */
export function telemetryKeyLevel(state: TelemetryKey["status"]): StatusLevel {
  switch (state) {
    case "active":
      return "neutral";
    case "retiring":
      return "warn";
    case "revoked":
      return "error";
  }
}

/**
 * The level for an audit-key pin. A pinned key is neutral (trusted, but not an app-health "green");
 * an observed-but-unpinned key WARNS (verification cannot trust it until approved — never green); a
 * retired key is unknown.
 */
export function auditKeyLevel(state: AuditKeyState): StatusLevel {
  switch (state) {
    case "pinned":
      return "neutral";
    case "observed_unpinned":
      return "warn";
    case "retired":
      return "unknown";
  }
}

/* ---- Liveness: REST baseline overlaid by the live stream ------------------------------------ */

/** The three liveness states the Source carries, plus the stream's live/silent overlay collapses onto. */
export type SourceLivenessState = Source["liveness"];

/**
 * Resolve a source's liveness, PREFERRING the live stream's state (fresher) over the REST snapshot.
 * The stream only knows `live` / `silent`; `never_seen` only ever comes from REST (a source that has
 * never reported is not in the live view). So: if the stream carries this source, use its state;
 * otherwise fall back to the REST `liveness`.
 */
export function resolveLiveness(
  restLiveness: SourceLivenessState,
  streamState: "live" | "silent" | undefined,
): SourceLivenessState {
  if (streamState !== undefined) return streamState;
  return restLiveness;
}

/* ---- Human labels --------------------------------------------------------------------------- */

export function sourceStatusLabel(status: SourceStatus): string {
  switch (status) {
    case "active":
      return "Active";
    case "disabled":
      return "Disabled";
    case "revoked":
      return "Revoked";
  }
}

export function livenessLabel(liveness: SourceLivenessState): string {
  switch (liveness) {
    case "live":
      return "Live";
    case "silent":
      return "Silent";
    case "never_seen":
      return "Never seen";
  }
}

export function telemetryKeyLabel(state: TelemetryKey["status"]): string {
  switch (state) {
    case "active":
      return "Active";
    case "retiring":
      return "Retiring";
    case "revoked":
      return "Revoked";
  }
}

export function auditKeyStateLabel(state: AuditKeyState): string {
  switch (state) {
    case "pinned":
      return "Pinned";
    case "observed_unpinned":
      return "Observed · not pinned";
    case "retired":
      return "Retired";
  }
}

export function auditKeyPolicyLabel(policy: AuditKeyPolicy): string {
  switch (policy) {
    case "pinned":
      return "Pinned keys";
    case "tofu":
      return "Trust on first use (demo)";
  }
}

export function reverificationStateLabel(state: Reverification["state"]): string {
  switch (state) {
    case "queued":
      return "Re-verification queued";
    case "running":
      return "Re-verifying stored batches";
    case "completed":
      return "Re-verification complete";
    case "failed":
      return "Re-verification failed";
  }
}

/**
 * The license-registration label for a source (design "Sources list" §4, Requirements 18.4, 18.6).
 *
 * A license-registered source carries the optional `registration` member (added additively to the
 * contract in task 12.3): `{ scope, instances, registered_at, last_registered_at }`. An
 * ADMIN-registered source has NO `registration` member, so `registrationLabel` returns `null` and
 * the screen renders NO tag for it — the one thing that tells the two apart on the list.
 *
 * The return is structured so the component renders a short visible tag plus a fuller accessible
 * title (design 12.4: convey the licensed state by TEXT and a `title`, never colour alone):
 * - `tag` is the short visible word `Licensed`;
 * - `title` is the full phrase: `Licensed · <tenant|user> key · <n> instance[s] · registered <time>`,
 *   with the singular `1 instance` and the plural `<n> instances`, and the registered time from the
 *   honest {@link formatTime} (so an unknown time reads "n/a", never a fabricated value).
 *
 * It reads only the signed/stored `registration` member; it invents nothing.
 */
export interface RegistrationLabel {
  /** The short visible tag text. Always the word `Licensed`. */
  readonly tag: string;
  /** The scope in words: `tenant key` for a tenant key, `user key` for a user key. */
  readonly scope: string;
  /** The instance count in words: `1 instance` (singular) or `<n> instances` (plural). */
  readonly instances: string;
  /** The fuller accessible title shown as the tag's `title` and announced as text. */
  readonly title: string;
}

export function registrationLabel(source: Source): RegistrationLabel | null {
  const registration = source.registration;
  // An admin-registered source has no `registration` member — no tag renders for it.
  if (registration === undefined) return null;

  const scope = registration.scope === "tenant" ? "tenant key" : "user key";
  const count = registration.instances;
  const instances = count === 1 ? "1 instance" : `${formatCount(count)} instances`;
  const registered = formatTime(registration.registered_at);
  const title = `Licensed · ${scope} · ${instances} · registered ${registered}`;

  return { tag: "Licensed", scope, instances, title };
}

/** A readable one-line summary of an admin deep-link's registration status (FR-1.5). */
export function adminLinkSummary(link: AdminLink): string {
  switch (link.registration) {
    case "registered":
      return link.url;
    case "none":
      return "No admin URL configured";
    case "unknown":
      return "Admin URL not yet known";
  }
}

/* ---- Honest formatters ---------------------------------------------------------------------- */

/** Format an epoch-ms timestamp as a local time, or "n/a" when null/unknown. */
export function formatTime(ms: number | null): string {
  if (ms === null || !Number.isFinite(ms)) return "n/a";
  return new Date(ms).toLocaleString();
}

/** Format an exact counter, or "n/a" when null. Counters are exact (design 12.3). */
export function formatCount(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return "n/a";
  return value.toLocaleString();
}

/** Format a last-sequence value, or "n/a" when the source has never reported one. */
export function formatSeq(seq: number | null): string {
  if (seq === null || !Number.isFinite(seq)) return "n/a";
  return `#${seq.toLocaleString()}`;
}

/**
 * Format a measured clock skew in ms as a signed, human span. Positive means the source clock is
 * ahead of the control plane; "n/a" when the server could not measure it (never shown as healthy).
 */
export function formatSkew(skewMs: number | null): string {
  if (skewMs === null || !Number.isFinite(skewMs)) return "n/a";
  const sign = skewMs > 0 ? "+" : skewMs < 0 ? "−" : "";
  const abs = Math.abs(skewMs);
  if (abs < 1000) return `${sign}${abs} ms`;
  const s = abs / 1000;
  if (s < 60) return `${sign}${s.toLocaleString(undefined, { maximumFractionDigits: 1 })} s`;
  const m = Math.floor(s / 60);
  const remS = Math.round(s % 60);
  return `${sign}${m} m ${String(remS).padStart(2, "0")} s`;
}

/** A short, stable truncation of an opaque id/thumbprint/kid for a dense cell. */
export function shortId(id: string, head = 10, tail = 6): string {
  if (id.length <= head + tail + 1) return id;
  return `${id.slice(0, head)}…${id.slice(-tail)}`;
}

/* ---- Error → sentence ----------------------------------------------------------------------- */

/**
 * Turn a failed WRITE into an operator-facing sentence. A 403 reads as "the server did not permit
 * this" (the write needs ADMIN; the server is the authority even when the UI hid the control); a
 * 400/422 is the server's own validation message shown verbatim (so a rejected JWK or an
 * out-of-policy input reads as the server returned it); a 409 is a conflict; a waking core defers to
 * the shell's retry.
 */
export function writeErrorMessage(error: ApiError, action: string): string {
  switch (error.kind) {
    case "forbidden":
      return `The server did not permit this ${action}. It requires the admin role; your role could not perform it.`;
    case "unauthorized":
      return "Your session has expired. Reload to sign in again.";
    case "not_found":
      return `The ${action} target no longer exists. Refresh and try again.`;
    case "network":
      return `The ${action} request could not be sent. Check your connection and try again.`;
    case "waking":
      return "The control plane is waking. The request will be retried shortly.";
    case "request":
    case "server":
    case "malformed":
    default:
      // A 400/422 carries the server's validation message (e.g. a rejected JWK, a conflicting kid,
      // or an out-of-policy environment) — show it so the admin sees exactly why.
      return error.message;
  }
}

/** Turn a failed READ into an operator-facing sentence (mirrors the other screens' read map). */
export function readErrorMessage(error: ApiError | null): string {
  if (error === null) return "The sources view could not load.";
  switch (error.kind) {
    case "unauthorized":
      return "Your session has expired. Reload to sign in again.";
    case "forbidden":
      return "You are not permitted to see sources. The server enforces access.";
    case "network":
      return "The sources request could not be sent. Check your connection.";
    default:
      return error.message;
  }
}
